/**
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business SNSコンテンツ生成のマルチアカウント対応で追加した、Web側が知っているアカウント定義（accountId・表示名の翻訳キー）の一覧。
 * バックエンドのアカウント定義（`apps/api/sns-content-generator/prompt/accounts/`）とaccountIdを一致させる必要がある。
 * 表示名（TikTok アカウント名）は固有名詞なので全言語で同じ値だが、他の UI 文言と同じく locales/<lang>/common.json に置く。
 * @graph-connects none
 */

/** SNSコンテンツ生成で選べる1アカウントの定義。 */
export interface SnsContentGeneratorAccount {
  /** アカウントを一意に識別するID。URLの `$accountId` パラメータ・API呼び出しにそのまま使う */
  id: string;
  /** タブ・見出し等に表示するアカウント名の翻訳キー（`snsContentGenerator.accounts.<id>`） */
  displayNameKey: string;
  /**
   * 画像生成枚数の入力欄の初期値。参考プロンプト（`reference.txt`）のページ構成が
   * アカウントごとに違うため、アカウント定義として持つ。
   * ユーザーは画面で MIN_IMAGE_PAGE_COUNT〜MAX_IMAGE_PAGE_COUNT の範囲に変更できる
   */
  defaultImagePageCount: number;
}

/**
 * SNSコンテンツ生成で選べるアカウントの一覧。表示順はタブの表示順と一致する。
 * @graph-connects none
 */
export const SNS_CONTENT_GENERATOR_ACCOUNTS: SnsContentGeneratorAccount[] = [
  {
    id: 'adult-akanuke-laboratory',
    displayNameKey: 'snsContentGenerator.accounts.adult-akanuke-laboratory',
    defaultImagePageCount: 5,
  },
  {
    id: 'oshi-color-closet',
    displayNameKey: 'snsContentGenerator.accounts.oshi-color-closet',
    defaultImagePageCount: 5,
  },
  {
    id: 'wavy-body-type-laboratory',
    displayNameKey: 'snsContentGenerator.accounts.wavy-body-type-laboratory',
    defaultImagePageCount: 7,
  },
];

/** URLに `accountId` が無いとき（`/sns-content-generator` 直打ち）にリダイレクトする既定アカウント。 @graph-connects none */
export const DEFAULT_SNS_CONTENT_GENERATOR_ACCOUNT_ID = 'adult-akanuke-laboratory';

/**
 * accountId がSNSコンテンツ生成で選べるアカウントかどうかを判定する。
 * URL直打ちで未知のaccountIdが渡された場合の入力チェックに使う。
 * @graph-connects none
 */
export function isKnownSnsContentGeneratorAccountId(accountId: string): boolean {
  return SNS_CONTENT_GENERATOR_ACCOUNTS.some((account) => account.id === accountId);
}

/** アカウント定義が見つからなかったときに使う画像生成枚数。既存アカウントと同じ値。 @graph-connects none */
const FALLBACK_IMAGE_PAGE_COUNT = 5;

/**
 * accountId に対応する画像生成枚数の初期値を返す。
 * 画面を描画するルートが未知のaccountIdを先に弾くため通常は必ず見つかるが、
 * 見つからない場合も画面が壊れないようフォールバック値を返す。
 * @graph-connects none
 */
export function getDefaultImagePageCount(accountId: string): number {
  const account = SNS_CONTENT_GENERATOR_ACCOUNTS.find((account) => account.id === accountId);
  return account?.defaultImagePageCount ?? FALLBACK_IMAGE_PAGE_COUNT;
}
